import React, { useState } from 'react';
import { skillSelectors, skillOperations } from 'store/skill';
import { Skill } from 'domain/Skill';
import { TextInput, Text } from '@patternfly/react-core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { usePageableData } from 'util/FunctionalComponentUtils';
import {
  DataTableUrlProps, RowEditButtons, RowViewButtons,
  setSorterInUrl, TableCell, TableRow, DataTable,
} from 'ui/components/DataTable';
import { tenantSelectors } from 'store/tenant';
import { stringSorter } from 'util/CommonSorters';
import { getPropsFromUrl } from 'util/BookmarkableUtils';
import { useValidators } from 'util/ValidationUtils';
import LoadingSpinner from 'ui/components/LoadingSpinner';
/* eslint-disable */

export type Props = RouteComponentProps;

export function SkillRow({ skill, setIsLoading } : { skill: Skill; setIsLoading: (loading: boolean) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('SkillsPage');

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await dispatch(skillOperations.removeSkill(skill));
      await dispatch(skillOperations.refreshSkillList());
    } catch (error) {
      console.error("Error al eliminar la habilidad:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isEditing) {
    return (<EditableSkillRow skill={skill} isNew={false} onClose={() => setIsEditing(false)} setIsLoading={setIsLoading} />);
  }

  return (
    <TableRow>
      <TableCell columnName={t('name')}>
        <Text>{skill.name}</Text>
      </TableCell>
      <RowViewButtons
        onEdit={() => setIsEditing(true)}
        onDelete={handleDelete}
      />
    </TableRow>
  );
}

export function EditableSkillRow(props: { skill: Skill; isNew: boolean; onClose: () => void; setIsLoading: (loading: boolean) => void; }) {
  const [name, setName] = useState(props.skill.name);
  const skillList = useSelector(skillSelectors.getSkillList);
  const dispatch = useDispatch();
  const { t } = useTranslation('SkillsPage');
  const validators = {
    nameMustNotBeEmpty: {
      predicate: (skill: Skill) => skill.name.length > 0,
      errorMsg: () => t('skillEmptyNameError'),
    },
    nameAlreadyTaken: {
      predicate: (skill: Skill) => skillList.filter((otherSkill) => otherSkill.name === skill.name
        && otherSkill.id !== skill.id).length === 0,
      errorMsg: (skill: Skill) => t('skillNameAlreadyTakenError', { name: skill.name }),
    },
  };

  const validationErrors = useValidators({
    ...props.skill,
    name,
  }, validators);

  const handleSave = async () => {
    props.setIsLoading(true);
    try {
      if (props.isNew) {
        await dispatch(skillOperations.addSkill({
          ...props.skill,
          name,
        }));
      } else {
        await dispatch(skillOperations.updateSkill({
          ...props.skill,
          name,
        }));
      }

      await dispatch(skillOperations.refreshSkillList());
    } catch (error) {
      console.error('Error al guardar usuario:', error);
    } finally {
      props.setIsLoading(false);
    }
  };


  return (
    <TableRow>
      <TableCell columnName={t('name')}>
        <TextInput value={name} onChange={setName} />
        {validationErrors.showValidationErrors('nameMustNotBeEmpty', 'nameAlreadyTaken')}
      </TableCell>
      <RowEditButtons
        isValid={validationErrors.isValid}
        onSave={handleSave}
        onClose={() => props.onClose()}
      />
    </TableRow>
  );
}

export const SkillsPage: React.FC<Props> = (props) => {
  const skillList = useSelector(skillSelectors.getSkillList);
  const tenantId = useSelector(tenantSelectors.getTenantId);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation('SkillsPage');

  const columns = [
    { name: t('name'), sorter: stringSorter<Skill>((skill) => skill.name) },
  ];

  const urlProps = getPropsFromUrl<DataTableUrlProps>(props, {
    page: '1',
    itemsPerPage: '10',
    filter: null,
    sortBy: '0',
    asc: 'true',
  });

  const sortBy = parseInt(urlProps.sortBy || '-1', 10);
  const { sorter } = columns[sortBy];

  const pageableData = usePageableData(urlProps, skillList, (skill) => [skill.name], sorter);

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <DataTable
        {...props}
        {...pageableData}
        title={t('skills')}
        columns={columns}
        rowWrapper={(skill) => (<SkillRow key={skill.id} skill={skill} setIsLoading={setIsLoading} />)}
        sortByIndex={sortBy}
        onSorterChange={(index) => setSorterInUrl(props, urlProps, sortBy, index)}
        newRowWrapper={(removeRow) => (
          <EditableSkillRow
            isNew
            onClose={removeRow}
            skill={{
              tenantId,
              name: '',
            }}
            setIsLoading={setIsLoading}
          />
        )}
      />
    </>
  );
};

export default withRouter(SkillsPage);
